import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subscription, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

// INTERFACES
import { IChannelHubspotDeal } from '../../../../channel';
import { IChannel } from '../../../interfaces/mtchannel.interface';
import { IPaginationResponse } from '../../../interfaces/paginator.interface';

// SERVICES
import { EnvironmentUtilService } from '../../util';
import { ApiService } from '../api.service';

@Injectable({
  providedIn: 'root',
})
export class ChannelApiService {
  private readonly _loading = new BehaviorSubject<boolean>(<boolean>false);
  private readonly _channelHubspotDeal = new BehaviorSubject<IChannelHubspotDeal>(
    <IChannelHubspotDeal>{},
  );

  loading$ = this._loading.asObservable();
  channelHubspotDeal$ = this._channelHubspotDeal.asObservable();

  constructor(
    private apiService :ApiService,
    private environmentUtilService :EnvironmentUtilService,
  ) {}
  environment = this.environmentUtilService.getEnvironment();

  getHubSpotDeal(dealId :number) :Subscription {
    return this.apiService
      .get(`${this.environment.urlV1}/channels/hubspot/deal/${dealId}`)
      .subscribe({
        next: (response :any) => {
          this._channelHubspotDeal.next(response);
        },
        error: (error) => throwError(() => new Error(error)),
      });
  }

  get(params :{ [param :string] :string | string[] }) :Observable<any> {
    //made it to ANY to be used as IChannel or IChannel[]
    return this.apiService.get(`${this.environment.urlV1}/channels`, {
      params,
    });
  }

  getV2(params :{
    [param :string] :string | string[];
  }) :Observable<IPaginationResponse> {
    //made it to ANY to be used as IChannel or IChannel[]
    return this.apiService.get(`${this.environment.urlV2}/channels`, {
      params,
    });
  }

  getById(
    id :number,
    params :{
      [param :string] :string | string[];
    },
  ) :Observable<IChannel> {
    return this.apiService
      .get(`${this.environment.urlV1}/channels/${id}`, { params })
      .pipe(
        map((response :IChannel) => {
          return response;
        }),
        catchError((response) => {
          return throwError(() => response.error);
        }),
      );
  }

  update(id :number, data :Partial<IChannel>) :Observable<any> {
    return this.apiService.post({
      path: `${this.environment.urlV1}/channels/${id}`,
      body: data,
    });
  }

  create(data :Partial<IChannel>) :Observable<any> {
    return this.apiService.post({
      path: `${this.environment.urlV1}/channels`,
      body: data,
    });
  }

  createV2(data :Partial<IChannel>) :Observable<any> {
    return this.apiService.post({
      path: `${this.environment.urlV2}/channels`,
      body: data,
    });
  }

  updateV2(params :{ id :number; data :Partial<IChannel> }) :Observable<any> {
    const { id, data } = params;

    return this.apiService.put(`${this.environment.urlV2}/channels/${id}`, data);
  }

  delete(id :number) :Observable<any> {
    return this.apiService.delete(`${this.environment.urlV2}/channels/${id}`);
  }

  updateStatus(data :{
    id :number;
    params :{ [param :string] :string | string[] };
  }) :Observable<any> {
    const { id, params } = data;

    return this.apiService.put(
      `${this.environment.urlV2}/channels/${id}/status`,
      params,
    );
  }

  getAll(params :{ [param :string] :string | string[] }) :Observable<any> {
    return this.apiService.get(`${this.environment.urlV2}/manage/channels`, {
      params,
    });
  }

  syncProductsByChannelIds(params :{
    [param :string] :string | string[];
  }) :Observable<any> {
    return this.apiService.get(
      `${this.environment.urlV2}/manage/channels/get-channel-products`,
      { params },
    );
  }

  syncProductsByAppIntegrationAccountIds(params :{
    [param :string] :string | string[];
  }) :Observable<any> {
    return this.apiService.get(
      `${this.environment.urlV2}/app-integration-accounts/sync-channel-products`,
      { params },
    );
  }

  renewQuickbooksToken(id :number) :Observable<any> {
    return this.apiService.get(
      `${this.environment.urlV1}/channels/${id}/quickbooks`,
    );
  }

  renewHubSpotTokens(id :number) :Observable<any> {
    return this.apiService.get(
      `${this.environment.urlV1}/channels/${id}/hubspot/authorize`,
    );
  }

  authorizeEcartApi(id :number) :Observable<any> {
    return this.apiService.get(
      `${this.environment.urlV1}/channels/${id}/ecartapi/authorize`,
    );
  }

  getDefaultPosChannel(params ?:{
    [param :string] :string | string[];
  }) :Observable<IChannel> {
    return this.apiService
      .get(`${this.environment.urlV2}/channels/default-pos-channel`, { params })
      .pipe(
        map((response :IChannel) => {
          return response;
        }),
        catchError((response) => {
          return throwError(() => response.error);
        }),
      );
  }
}
